import { LegalDocumentVersion } from '@models/LegalDocument'
import { legalDocumentVersionSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createLegalDocumentVersion as createLegalDocumentVersionAPI,
  deleteLegalDocumentVersion as deleteLegalDocumentVersionAPI,
  fetchLegalDocumentVersions,
  updateLegalDocumentVersion as updateLegalDocumentVersionAPI,
} from '@services/LegalDocumentVersionService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<LegalDocumentVersion> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'legalDocumentVersion',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getLegalDocumentVersionsSuccess,
  setQuery: setLegalDocumentVersionQuery,
  reset: resetLegalDocumentVersionState,
  resetQueryAndIds: resetLegalDocumentVersionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: LegalDocumentVersion }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectLegalDocumentVersions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedLegalDocumentVersion = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectLegalDocumentVersionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectLegalDocumentVersionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedLegalDocumentVersion =
  (legalDocumentVersion: LegalDocumentVersion | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<LegalDocumentVersion>(legalDocumentVersion)
      )
    )

export const getLegalDocumentVersions = (
  legalDocumentSlug: string,
  params: TableParams
) =>
  SBAPI.FetchPaginatedDispatched(
    fetchLegalDocumentVersions(legalDocumentSlug, params),
    [legalDocumentVersionSchema],
    slice.actions
  )

export const createLegalDocumentVersion = (
  legalDocumentSlug: string,
  legalDocumentVersion: LegalDocumentVersion
) =>
  SBAPI.CreateDispatched(
    createLegalDocumentVersionAPI(legalDocumentSlug, legalDocumentVersion),
    legalDocumentVersionSchema,
    slice.actions
  )

export const updateLegalDocumentVersion = (
  legalDocumentSlug: string,
  legalDocumentVersion: LegalDocumentVersion
) =>
  SBAPI.UpdateDispatched<LegalDocumentVersion>(
    updateLegalDocumentVersionAPI(legalDocumentSlug, legalDocumentVersion),
    legalDocumentVersion,
    slice.actions
  )

export const deleteLegalDocumentVersion = (
  legalDocumentSlug: string,
  legalDocumentVersion: LegalDocumentVersion
) =>
  SBAPI.DestroyDispatched<LegalDocumentVersion>(
    deleteLegalDocumentVersionAPI(legalDocumentSlug, legalDocumentVersion),
    legalDocumentVersion,
    slice.actions
  )
